import { useEffect, useState } from "react";
import {
    FileText, ChatCircleDots, Coins, FolderPlus, Scales,
    User, Sparkle, PaperPlaneTilt, CheckCircle,
} from "@phosphor-icons/react";

const STEPS = [
    { key: "case", label: "01 — Open the case", title: "We capture the facts" },
    { key: "draft", label: "02 — We draft the letter", title: "Validation, dispute or settlement" },
    { key: "coach", label: "03 — Practice the call", title: "Role-play with the AI coach" },
    { key: "settle", label: "04 — Run the math", title: "Statutory interest + payment plan" },
];

const DURATION = 4200;

export default function HowItWorksDemo() {
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setIdx((i) => (i + 1) % STEPS.length), DURATION);
        return () => clearInterval(t);
    }, []);

    return (
        <div
            className="relative aspect-[4/5] rounded-md overflow-hidden border border-slate-200 bg-[#0F1E3D]"
            data-testid="how-it-works-demo"
        >
            {/* Browser chrome */}
            <div className="absolute top-0 left-0 right-0 h-9 bg-[#0B1530] border-b border-white/10 flex items-center px-4 gap-2 z-20">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-300/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
                <span className="ml-3 text-[10px] uppercase tracking-[0.18em] text-white/50">lienlift-direct.ai / case</span>
            </div>

            {/* Stage */}
            <div className="absolute inset-0 pt-12 pb-20 px-5 sm:px-7">
                <div className="relative w-full h-full">
                    <Frame active={idx === 0}><CaseFrame /></Frame>
                    <Frame active={idx === 1}><DraftFrame /></Frame>
                    <Frame active={idx === 2}><CoachFrame /></Frame>
                    <Frame active={idx === 3}><SettleFrame /></Frame>
                </div>
            </div>

            {/* Footer caption + step dots */}
            <div className="absolute bottom-0 left-0 right-0 px-6 pb-5 z-20">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/50">{STEPS[idx].label}</p>
                <p className="font-serif text-xl sm:text-2xl text-white mt-1">{STEPS[idx].title}</p>
                <div className="flex gap-1 mt-3">
                    {STEPS.map((s, i) => (
                        <button
                            key={s.key}
                            aria-label={s.label}
                            onClick={() => setIdx(i)}
                            data-testid={`demo-dot-${i}`}
                            className={`h-1 rounded-full transition-all ${i === idx ? "w-10 bg-white" : "w-4 bg-white/30 hover:bg-white/50"}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function Frame({ active, children }) {
    return (
        <div
            className={`absolute inset-0 transition-all duration-700 ease-out ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`}
        >
            {children}
        </div>
    );
}

/* ----- Frame 1: New case form filling in ----- */
function CaseFrame() {
    const fields = [
        { label: "Title", value: "Discover Bank v. Garcia", delay: 0 },
        { label: "Creditor", value: "Discover Bank", delay: 250 },
        { label: "Amount", value: "$14,820", delay: 500 },
        { label: "Jurisdiction", value: "CA — Los Angeles", delay: 750 },
    ];
    return (
        <div className="text-white">
            <div className="flex items-center gap-2 text-white/70">
                <FolderPlus size={16} weight="duotone" />
                <span className="text-xs uppercase tracking-[0.18em]">New case</span>
            </div>
            <div className="mt-5 space-y-2.5">
                {fields.map((f) => (
                    <div
                        key={f.label}
                        className="bg-white/[0.06] border border-white/10 rounded-md px-3 py-2.5 opacity-0 animate-[fadeUp_0.5s_ease-out_forwards]"
                        style={{ animationDelay: `${f.delay}ms` }}
                    >
                        <p className="text-[9px] uppercase tracking-[0.18em] text-white/40">{f.label}</p>
                        <p className="text-sm text-white mt-0.5">{f.value}</p>
                    </div>
                ))}
            </div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-emerald-300 opacity-0 animate-[fadeUp_0.5s_ease-out_1100ms_forwards]">
                <CheckCircle size={14} weight="fill" /> Case opened
            </div>
            <style>{keyframes}</style>
        </div>
    );
}

/* ----- Frame 2: Letter being typed ----- */
function DraftFrame() {
    const lines = [
        "Re: Account #4321-9999",
        "",
        "To Whom It May Concern,",
        "I am writing to request validation of the",
        "above-referenced account pursuant to",
        "FDCPA §809(b).",
        "",
        "Please provide an itemized accounting,",
        "chain-of-title and the original signed",
        "agreement within 30 days.",
        "",
        "Sincerely,",
        "[CLIENT NAME]",
    ];
    return (
        <div className="text-white">
            <div className="flex items-center gap-2 text-white/70">
                <FileText size={16} weight="duotone" />
                <span className="text-xs uppercase tracking-[0.18em]">Validation letter · Drafting</span>
                <Sparkle size={12} weight="fill" className="text-[#7AB1FF] ml-1 animate-pulse" />
            </div>
            <div className="mt-4 bg-white/95 rounded-md p-4 text-slate-800 font-mono text-[11px] leading-relaxed shadow-lg">
                {lines.map((l, i) => (
                    <p
                        key={i}
                        className="opacity-0 animate-[fadeUp_0.35s_ease-out_forwards]"
                        style={{ animationDelay: `${i * 120}ms`, minHeight: "1em" }}
                    >
                        {l || "\u00A0"}
                    </p>
                ))}
            </div>
            <p className="text-[10px] italic text-white/50 mt-2">Drafted at your direction. You sign and send.</p>
        </div>
    );
}

/* ----- Frame 3: Coach chat ----- */
function CoachFrame() {
    const turns = [
        { role: "creditor", text: "We can settle for 80% of the balance — $11,856." },
        { role: "user", text: "I can offer 25% — $3,700 — paid in full this week." },
        { role: "coach", text: "Strong anchor. Hold silent next. The first to speak loses leverage." },
    ];
    return (
        <div className="text-white">
            <div className="flex items-center gap-2 text-white/70">
                <ChatCircleDots size={16} weight="duotone" />
                <span className="text-xs uppercase tracking-[0.18em]">Negotiation coach</span>
            </div>
            <div className="mt-4 space-y-2.5">
                {turns.map((t, i) => (
                    <div
                        key={i}
                        className={`flex ${t.role === "user" ? "justify-end" : "justify-start"} opacity-0 animate-[fadeUp_0.4s_ease-out_forwards]`}
                        style={{ animationDelay: `${i * 600}ms` }}
                    >
                        <div
                            className={`max-w-[85%] rounded-md px-3 py-2 text-xs leading-snug ${
                                t.role === "user"
                                    ? "bg-white text-[#0F1E3D] font-medium"
                                    : t.role === "coach"
                                    ? "bg-[#2563EB]/20 border border-[#2563EB]/40 text-white"
                                    : "bg-white/[0.07] border border-white/10 text-white/80"
                            }`}
                        >
                            <span className="block text-[9px] uppercase tracking-[0.14em] opacity-60 mb-0.5">
                                {t.role === "user" ? "You" : t.role === "coach" ? "Coach" : "Creditor"}
                            </span>
                            {t.text}
                        </div>
                    </div>
                ))}
            </div>
            <style>{keyframes}</style>
        </div>
    );
}

/* ----- Frame 4: Judgment math + settlement ----- */
function SettleFrame() {
    return (
        <div className="text-white">
            <div className="flex items-center gap-2 text-white/70">
                <Coins size={16} weight="duotone" />
                <span className="text-xs uppercase tracking-[0.18em]">Judgment interest · CA 10%</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
                {[
                    { l: "Original judgment", v: "$10,000" },
                    { l: "Interest (1,247d)", v: "$3,416" },
                    { l: "Total owed today", v: "$13,416", hi: true },
                    { l: "Settlement target", v: "$4,150", hi2: true },
                ].map((s, i) => (
                    <div
                        key={s.l}
                        className={`rounded-md p-3 border opacity-0 animate-[fadeUp_0.45s_ease-out_forwards] ${
                            s.hi ? "bg-white text-[#0F1E3D] border-white" :
                            s.hi2 ? "bg-emerald-400/20 border-emerald-300/50 text-white" :
                            "bg-white/[0.06] border-white/10"
                        }`}
                        style={{ animationDelay: `${i * 220}ms` }}
                    >
                        <p className={`text-[9px] uppercase tracking-[0.18em] ${s.hi ? "text-slate-500" : "text-white/50"}`}>{s.l}</p>
                        <p className="font-mono text-base mt-1">{s.v}</p>
                    </div>
                ))}
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-emerald-300 opacity-0 animate-[fadeUp_0.4s_ease-out_1100ms_forwards]">
                <PaperPlaneTilt size={14} weight="fill" /> Settlement offer drafted &amp; ready to sign
            </div>
            <style>{keyframes}</style>
        </div>
    );
}

const keyframes = `
@keyframes fadeUp {
    0% { opacity: 0; transform: translateY(6px); }
    100% { opacity: 1; transform: translateY(0); }
}
`;
